import React, { useState, useEffect, PropsWithChildren } from 'react';
import {
  Box,
  Theme,
  CircularProgress,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';

import {
  CasesPanel,
  PanelState,
  CasesPanelProps,
  NARROW_SCREEN_BREAKPOINT,
} from './CasesPanel';
import { CasesContent } from './CasesContent';
import { useCases } from '../../contexts/api-contexts/CasesContext';

// Panel state to width mapping
const PANEL_WIDTHS = {
  collapsed: 60,
  default: 320,
  expanded: 450,
};

interface StyleProps {
  isStandalone: boolean;
}

const useStyles = makeStyles<StyleProps>()(
  (theme: Theme, { isStandalone }) => ({
    casesLayoutContainer: {
      display: 'flex',
      width: '100%',
      // When standalone, use 100% height to fill the container; otherwise subtract AppBar height
      height: isStandalone ? '100%' : 'calc(100vh - 64px)',
      backgroundColor: theme.palette.background.default,
      // When standalone, position relative to contain absolute children
      position: isStandalone ? 'relative' : undefined,
      overflow: isStandalone ? 'hidden' : undefined,
    },
    loadingContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      width: '100%',
      height: '100%',
      gap: theme.spacing(2),
    },
    loadingText: {
      color: theme.palette.text.secondary,
    },
  })
);

interface CasesLayoutProps extends PropsWithChildren {
  casesPanelProps?: CasesPanelProps;
  /** When true, layout is used standalone (e.g., embedded in another app) and should be self-contained */
  isStandalone?: boolean;
}

export const CasesLayout: React.FC<CasesLayoutProps> = ({
  children,
  casesPanelProps,
  isStandalone = false,
}) => {
  const { classes } = useStyles({ isStandalone });
  const { currentCase } = useCases();

  // Detect narrow screens - on narrow screens panel overlays content
  const isNarrowScreen = useMediaQuery(
    `(max-width: ${NARROW_SCREEN_BREAKPOINT}px)`
  );

  // On narrow screens, start collapsed; on wider screens, start with default
  const [panelState, setPanelState] = useState<PanelState>(() =>
    isNarrowScreen ? 'collapsed' : 'default'
  );

  // On narrow screens, content always uses collapsed width (panel overlays)
  // On wider screens, content width follows panel state
  const effectivePanelWidth = isNarrowScreen
    ? PANEL_WIDTHS.collapsed
    : PANEL_WIDTHS[panelState];

  const [panelWidth, setPanelWidth] = useState(effectivePanelWidth);

  // Update panel width when state or screen size changes
  useEffect(() => {
    setPanelWidth(effectivePanelWidth);
  }, [effectivePanelWidth]);

  // Reset panel state when screen size changes
  useEffect(() => {
    if (isNarrowScreen && panelState === 'default') {
      setPanelState('collapsed');
    }
  }, [isNarrowScreen, panelState]);

  // Listen for panel state changes from child components
  const handlePanelStateChange = (newState: PanelState) => {
    setPanelState(newState);
  };

  // Check if we should show loading state
  const shouldShowLoading =
    currentCase &&
    (currentCase.status === 'PENDING' || currentCase.status === 'PROCESSING') &&
    (!currentCase.notes || currentCase.notes.trim() === '');

  // Show loading state if case is being created/processed
  if (shouldShowLoading) {
    return (
      <Box className={classes.casesLayoutContainer}>
        <Box className={classes.loadingContainer}>
          <CircularProgress size={48} />
          <Typography variant="h6" className={classes.loadingText}>
            Processing case, please wait...
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box className={classes.casesLayoutContainer}>
      {/* Main content area with router outlet */}
      <CasesContent panelWidth={panelWidth} isStandalone={isStandalone}>
        {children}
      </CasesContent>

      {/* Right panel */}
      <CasesPanel
        panelState={panelState}
        onPanelStateChange={handlePanelStateChange}
        isNarrowScreen={isNarrowScreen}
        isStandalone={isStandalone}
        {...casesPanelProps}
      />
    </Box>
  );
};
